import { FC, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, Link, useNavigate, useParams } from 'react-router'
import { Navigate } from 'react-router-dom'

import { CalendarOutlined } from '@ant-design/icons'
import { Button, Flex, Form, Select, Timeline, Typography } from 'antd'
import { useResponsive } from 'antd-style'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { createEvents, EventAttributes } from 'ics'

import { useCountries } from '../../components/country'
import { Country } from '../../components/icon/Country'
import { Page } from '../../components/page'
import { Title } from '../../components/text/Title'
import { useRacesQuery } from '../../graphql'
import routes, { generateSlug } from '../../routes'

dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.tz.setDefault('Europe/Kiev')

async function handleDownload(type: string, events: EventAttributes[]) {
  const filename = `MotoGP Calendar 2025 [${type}].ics`
  const file = await new Promise<Blob>((resolve, reject) => {
    createEvents(events, (error, value) => {
      if (error) {
        reject(error)
      }

      resolve(new File([value], filename, { type: 'text/calendar' }))
    })
  })
  const url = URL.createObjectURL(file)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(url)
}

const translations: { [key: string]: RaceClass } = {
  ['motogp']: 'MotoGP',
  ['moto2']: 'Moto2',
  ['moto3']: 'Moto3',
}

const currentDate = dayjs().toDate()

const Calendar: FC = () => {
  const { year, category } = useParams()
  const { formatMessage } = useIntl()
  const { getNameByIso } = useCountries()
  const { mobile } = useResponsive()
  const navigate = useNavigate()
  const [racingClass, setRacingClass] = useState<RaceClass>(category ? translations[category] : 'MotoGP')
  const [racingType, setRacingType] = useState<RaceType>('RACE')

  const { data, loading } = useRacesQuery({
    variables: { racingClass, racingType },
  })

  const events = useMemo(
    () =>
      (data?.races ?? [])
        .slice()
        .filter((it) => it?.schedule?.[0]?.startDate)
        .sort((a, b) => (dayjs(a?.schedule?.[0]?.startDate).diff(b?.schedule?.[0]?.startDate) > 0 ? 1 : -1)),
    [data?.races],
  )

  useEffect(() => {
    const selectedCategory = Object.entries(translations).find(([, value]) => value == racingClass)?.[0]
    if (selectedCategory && selectedCategory !== category) {
      navigate(
        generatePath(routes.calendar, {
          category: selectedCategory,
          year: '2025',
        }),
      )
    }
  }, [category, navigate, racingClass])

  if (!year || !category) return <Navigate to={generatePath(routes.calendar, { category: 'motogp', year: '2025' })} />

  const title = [formatMessage({ id: 'calendar.header' }), translations[category], year].join(' ')

  return (
    <Page
      loading={loading}
      title={title}
      seo={{ metaTitle: [title, 'TochkaGP'].join(' | ') }}
      extra={
        <Form initialValues={{ racingClass, racingType }} layout={'inline'} style={{ marginBottom: 16 }}>
          <Flex vertical={mobile} gap={mobile ? 16 : 0}>
            <Flex>
              <Form.Item name={'racingClass'}>
                <Select<RaceClass>
                  onChange={setRacingClass}
                  options={Object.values(translations).map((it) => ({
                    label: <Typography.Text code>{it}</Typography.Text>,
                    value: it,
                  }))}
                />
              </Form.Item>
              <Form.Item name={'racingType'}>
                <Select<RaceType>
                  onChange={setRacingType}
                  options={[
                    {
                      label: <Typography.Text code>{formatMessage({ id: 'calendar.sprint' })}</Typography.Text>,
                      value: 'SPRINT',
                    },
                    {
                      label: <Typography.Text code>{formatMessage({ id: 'calendar.race' })}</Typography.Text>,
                      value: 'RACE',
                    },
                  ]}
                />
              </Form.Item>
            </Flex>
            <Button
              icon={<CalendarOutlined />}
              onClick={() =>
                handleDownload(
                  racingType,
                  events.map((it) => {
                    const date = dayjs(it?.schedule?.[0]?.startDate)
                      .tz(it?.track?.timezone ?? undefined, true)
                      .local()
                    const year = date.year()
                    const month = Number.parseInt(date.format('M'))
                    const day = Number.parseInt(date.format('D'))
                    const hour = Number.parseInt(date.format('H'))
                    const minute = Number.parseInt(date.format('m'))

                    return {
                      start: minute > 0 ? [year, month, day, hour, minute] : [year, month, day, hour],
                      startInputType: 'local',
                      title: `🏁 [${[it?.schedule?.[0]?.racingClass, it?.schedule?.[0]?.type].join(' ')}] ${it?.track?.name}`,
                      duration: { hours: 1 },
                      location: `${it?.track?.name}, ${it?.track?.country ? getNameByIso(it?.track?.country) : ''}`,
                      alarms: [{ action: 'display', trigger: { before: true, hours: 1, minutes: 0 } }],
                      url: [`https://tochkagp.com`, generatePath(routes.track, { slug: `${it?.track?.slug}` })].join(
                        '',
                      ),
                    }
                  }),
                )
              }
            >
              {formatMessage({ id: 'calendar.download' })}
            </Button>
          </Flex>
        </Form>
      }
    >
      <Timeline
        mode={'left'}
        items={events.map((it) => {
          return {
            color: dayjs(it?.schedule?.[0]?.startDate).isAfter(dayjs()) ? 'green' : 'gray',
            children: (
              <Flex vertical>
                <Typography.Text>
                  {dayjs(it?.schedule?.[0]?.startDate)
                    .tz(it?.track?.timezone ?? undefined, true)
                    .local()
                    .format('DD MMMM, HH:mm')}
                </Typography.Text>
                <Title style={{ margin: '8px 0' }} level={4}>
                  <Flex gap={8} align={'center'}>
                    <Country height={16} country={it?.track?.country} />
                    <Link to={generatePath(routes.track, { slug: `${it?.track?.slug}` })}>{it?.track?.name}</Link>
                  </Flex>
                </Title>
                <Link
                  to={generatePath(routes.result, {
                    year: currentDate.getFullYear().toString(),
                    track: `${it?.track?.slug}`,
                    category: generateSlug(racingClass),
                  })}
                >
                  {formatMessage({ id: 'results.header' })}
                </Link>
              </Flex>
            ),
          }
        })}
      />
    </Page>
  )
}

export { Calendar as default }
